import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronUp, IconExternalLink, IconPencil } from '@tabler/icons-react';

import { useAppSelector } from '@/store/hooks';

import { loadClinicLetters, loadLetterVolume, openLetterEditor } from './clinicLetterClient';
import {
  ClinicLetters,
  LETTER_SHORT_CODES,
  LETTER_UNIT_COST_USD,
  letterClinics,
  letterMeta,
  LetterType,
  provisionedLetters
} from './letterTemplates';

import type { LetterVolume } from './clinicLetterClient';

const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const numberFmt = new Intl.NumberFormat('en-US');

function monthStart(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}-01`;
}

function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

interface EditorState {
  displayName: string;
  url: string;
}

const LetterTemplatesSection = () => {
  const user = useAppSelector((state) => state.auth.user);
  const clinics = letterClinics(user?.organizations);
  const [clinicId, setClinicId] = useState(clinics[0]?.id ?? '');
  const [letters, setLetters] = useState<ClinicLetters | null>(null);
  const [loading, setLoading] = useState(false);
  const [openingType, setOpeningType] = useState<LetterType | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [showCodes, setShowCodes] = useState(false);
  const [volume, setVolume] = useState<LetterVolume | null>(null);
  const [range, setRange] = useState({ from: monthStart(), to: today() });

  const activeClinicId = clinics.some((clinic) => clinic.id === clinicId)
    ? clinicId
    : (clinics[0]?.id ?? '');
  const activeClinicRef = useRef(activeClinicId);
  activeClinicRef.current = activeClinicId;

  useEffect(() => {
    if (!activeClinicId) return;
    let cancelled = false;
    setLoading(true);
    setEditor(null);
    void loadClinicLetters(activeClinicId)
      .then((loaded) => {
        if (cancelled) return;
        setLetters(loaded.letters);
      })
      .catch((error) => {
        if (cancelled) return;
        setLetters(null);
        notifications.show({
          title: 'Could not load letter templates',
          message: error instanceof Error ? error.message : 'Try again.',
          color: 'red'
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeClinicId]);

  const rows = provisionedLetters(letters);
  const templateIds = rows
    .map((row) => row.postgridTemplateId)
    .filter((id): id is string => Boolean(id));

  const templateKey = templateIds.join(',');

  useEffect(() => {
    if (!activeClinicId || templateIds.length === 0) {
      setVolume(null);
      return;
    }
    let cancelled = false;
    void loadLetterVolume(activeClinicId, templateIds, range.from, range.to)
      .then((next) => {
        if (!cancelled) setVolume(next);
      })
      .catch(() => {
        if (!cancelled) setVolume(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeClinicId, templateKey, range.from, range.to]);

  const edit = async (letterType: LetterType, displayName: string, templateId: string) => {
    const organizationId = activeClinicId;
    if (!organizationId) return;
    setOpeningType(letterType);
    try {
      const session = await openLetterEditor(organizationId, letterType, templateId, false);
      if (activeClinicRef.current !== organizationId) return;
      setEditor({ displayName, url: session.url });
    } catch (error) {
      notifications.show({
        title: 'Could not open the editor',
        message: error instanceof Error ? error.message : 'Try again.',
        color: 'red'
      });
    } finally {
      setOpeningType(null);
    }
  };

  const spend = (count: number) =>
    currencyFmt.format(count * (volume?.unitCostUsd || LETTER_UNIT_COST_USD));

  return (
    <Box>
      <Text fw={700} size="lg" mb={4}>
        Letter templates
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        These are the letters Rhythm has set up for a clinic you belong to. You can edit the
        content.
      </Text>

      {clinics.length === 0 ? (
        <Alert color="gray" variant="light">
          No clinic is assigned to this account.
        </Alert>
      ) : (
        <Stack gap="md">
          <Select
            maw={420}
            label="Clinic"
            data={clinics.map((clinic) => ({ value: clinic.id, label: clinic.name }))}
            value={activeClinicId}
            onChange={(value) => value && setClinicId(value)}
            allowDeselect={false}
          />

          {loading ? (
            <Loader size="sm" />
          ) : !letters?.lettersEnabled ? (
            <Alert color="gray" variant="light">
              Letter templates are not turned on for this clinic. Contact your Rhythm
              representative.
            </Alert>
          ) : rows.length === 0 ? (
            <Alert color="gray" variant="light">
              No letter templates have been set up for this clinic yet.
            </Alert>
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <Stat
                  label="This month"
                  count={volume?.mtd ?? 0}
                  amount={spend(volume?.mtd ?? 0)}
                />
                <Stat
                  label="Last month"
                  count={volume?.lastMonth ?? 0}
                  amount={spend(volume?.lastMonth ?? 0)}
                />
                <Stat
                  label="Selected dates"
                  count={volume?.rangeCount ?? 0}
                  amount={spend(volume?.rangeCount ?? 0)}
                />
              </SimpleGrid>
              <Group align="flex-end">
                <TextInput
                  type="date"
                  label="From"
                  value={range.from}
                  onChange={(event) =>
                    setRange((current) => ({ ...current, from: event.currentTarget.value }))
                  }
                />
                <TextInput
                  type="date"
                  label="To"
                  value={range.to}
                  onChange={(event) =>
                    setRange((current) => ({ ...current, to: event.currentTarget.value }))
                  }
                />
              </Group>
              {volume?.truncated && (
                <Text size="xs" c="dimmed">
                  The counts above stop after the most recent letters.
                </Text>
              )}

              <Button
                variant="subtle"
                color="plum"
                size="compact-sm"
                px={0}
                rightSection={
                  showCodes ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                }
                onClick={() => setShowCodes((open) => !open)}>
                Short codes
              </Button>
              <Collapse in={showCodes}>
                <Paper withBorder>
                  <Table>
                    <Table.Tbody>
                      {LETTER_SHORT_CODES.map((code) => (
                        <Table.Tr key={code.code}>
                          <Table.Td>
                            <Text size="sm" fw={600}>
                              {code.code}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {code.description}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Collapse>

              {rows.map((row) => {
                const meta = letterMeta(row.letterType);
                return (
                  <Card key={row.letterType} withBorder padding="md">
                    <Box>
                      <Text fw={600}>{meta.label}</Text>
                      <Text size="sm" c="dimmed">
                        {meta.whenItSends}
                      </Text>
                    </Box>
                    <Group mt="sm">
                      <Button
                        size="compact-sm"
                        variant="light"
                        color="plum"
                        leftSection={<IconPencil size={14} />}
                        loading={openingType === row.letterType}
                        onClick={() => {
                          if (!row.postgridTemplateId) return;
                          void edit(row.letterType, meta.label, row.postgridTemplateId);
                        }}>
                        Edit content
                      </Button>
                    </Group>
                  </Card>
                );
              })}
            </>
          )}
        </Stack>
      )}

      <Modal
        opened={editor !== null}
        onClose={() => setEditor(null)}
        size="900px"
        title={
          <Group gap="sm">
            <Text fw={700}>Edit content — {editor?.displayName}</Text>
            {editor && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="plum"
                component="a"
                href={editor.url}
                target="_blank"
                rel="noopener noreferrer"
                leftSection={<IconExternalLink size={13} />}>
                Open in new tab
              </Button>
            )}
          </Group>
        }
        styles={{ body: { padding: 0 } }}>
        {editor && (
          <iframe
            title={`PostGrid editor — ${editor.displayName}`}
            src={editor.url}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        )}
      </Modal>
    </Box>
  );
};

function Stat({ label, count, amount }: { label: string; count: number; amount: string }) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text fw={700} size="xl">
        {numberFmt.format(count)}
      </Text>
      <Text size="sm" c="dimmed">
        {amount}
      </Text>
    </Paper>
  );
}

export default LetterTemplatesSection;
